// js/components/ExpandableManager.js
import Delegate from 'ftdomdelegate';
import { Animation } from '../utils.js';

export class ExpandableManager extends HTMLElement {
  constructor() {
    super();
    this.domDelegate = new Delegate(this);
    this._attachListeners();
  }

  connectedCallback() {
    // Process expandable content elements
    this._processCollapsibles();
    
    // Set up a mutation observer to reprocess when content changes
    this._setupMutationObserver();
  }

  disconnectedCallback() {
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
    }
  }

  _setupMutationObserver() {
    this._mutationObserver = new MutationObserver((mutations) => {
      let shouldReprocess = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          shouldReprocess = true;
        }
      });
      
      if (shouldReprocess) {
        // Use a small delay to ensure DOM is fully updated
        setTimeout(() => {
          this._processCollapsibles();
        }, 100);
      }
    });
    
    this._mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded']
    });
  }

  _attachListeners() {
    this.domDelegate.on("click", '[data-action="toggle-expandable"]', this._toggleExpandableContent.bind(this));
  }

  /**
   * Process collapsible elements to determine if they should be expandable
   */
  _processCollapsibles() {
    const expandableElements = this.querySelectorAll("[expandable-content]");
    
    expandableElements.forEach((item) => {
      // Only process elements that have aria-expanded attribute
      if (!item.hasAttribute("aria-expanded")) {
        return;
      }
      
      // Temporarily remove all height restrictions to get true content height
      const originalMaxHeight = item.style.maxHeight;
      const originalHeight = item.style.height;
      const originalOverflow = item.style.overflow;
      
      item.style.maxHeight = 'none';
      item.style.height = 'auto';
      item.style.overflow = 'visible';
      
      // Force reflow to ensure accurate measurements
      item.offsetHeight;
      
      const trueScrollHeight = item.scrollHeight;
      
      // Restore original styles
      item.style.maxHeight = originalMaxHeight;
      item.style.height = originalHeight;
      item.style.overflow = originalOverflow;
      
      // Force reflow again to get the current client height
      item.offsetHeight;
      const currentClientHeight = item.clientHeight;
      
      // Check if content is taller than the current visible area
      // Use a tolerance of 50px to account for potential rounding issues
      if (trueScrollHeight > currentClientHeight + 50) {
        item.classList.add("expandable-content--expandable");
        // Set initial max-height for collapsed state
        if (item.getAttribute("aria-expanded") === "false") {
          item.style.maxHeight = `${currentClientHeight}px`;
        }
      } else {
        item.setAttribute("aria-expanded", "true");
        item.classList.remove("expandable-content--expandable");
      }
    });
  }

  /**
   * Reprocess collapsibles when content changes
   */
  reprocessCollapsibles() {
    this._processCollapsibles();
  }

  /**
   * Toggle expandable content
   */
  _toggleExpandableContent(event, expandableButton) {
    const parentSection = this.querySelector("[expandable-content]");
    if (!parentSection) return;

    if (parentSection.getAttribute("aria-expanded") === "true") {
      // Close
      parentSection.setAttribute("aria-expanded", "false");
      
      // Calculate the collapsed height (visible content height)
      // Temporarily remove height restrictions to get true content height
      const originalMaxHeight = parentSection.style.maxHeight;
      const originalHeight = parentSection.style.height;
      const originalOverflow = parentSection.style.overflow;
      
      parentSection.style.maxHeight = 'none';
      parentSection.style.height = 'auto';
      parentSection.style.overflow = 'visible';
      
      // Force reflow
      parentSection.offsetHeight;
      
      const trueScrollHeight = parentSection.scrollHeight;
      
      // Restore original styles
      parentSection.style.maxHeight = originalMaxHeight;
      parentSection.style.height = originalHeight;
      parentSection.style.overflow = originalOverflow;
      
      // Force reflow again to get current client height
      parentSection.offsetHeight;
      const currentClientHeight = parentSection.clientHeight;
      
      // Set collapsed height to current visible height
      parentSection.style.maxHeight = `${currentClientHeight}px`;
      
      // Force reflow
      parentSection.offsetHeight;
      
      // Animate to collapsed state
      parentSection.style.maxHeight = null;
      
      const expandableText = expandableButton.querySelector("[data-view-more]");
      if (expandableText) {
        expandableText.innerHTML = expandableText.getAttribute("data-view-more");
      }
      
      const parentCard = parentSection.closest(".card");
      if (parentCard) {
        const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
        const amountToScroll = parentCard.getBoundingClientRect().top - 15 - headerHeight;
        window.scrollBy({ top: amountToScroll, behavior: "smooth" });
      }
    } else {
      // Open
      parentSection.setAttribute("aria-expanded", "true");
      
      // Remove any margin adjustments
      if (expandableButton.previousElementSibling) {
        expandableButton.previousElementSibling.style["margin-bottom"] = "";
      }
      
      // Animate to expanded state
      Animation.slideDown(parentSection, null, "max-height");
      
      const expandableText = expandableButton.querySelector("[data-view-more]");
      if (expandableText) {
        expandableText.innerHTML = expandableText.getAttribute("data-view-less");
      }
    }
  }
}

if (!customElements.get("cos-expandable")) customElements.define("cos-expandable", ExpandableManager);

// Custom element for farmacii expandable functionality
export class ExpandableFarmaciiManager extends HTMLElement {
  constructor() {
    super();
    this.domDelegate = new Delegate(this);
    this._attachListeners();
  }

  connectedCallback() {
    // Process expandable content elements
    // this._processCollapsibles();
  }

  _attachListeners() {
    this.domDelegate.on("click", '[data-action="toggle-expandable"]', this._toggleExpandableContent.bind(this));
  }

  /**
   * Process collapsible elements to determine if they should be expandable
   */
  _processCollapsibles() {
    this.querySelectorAll("[expandable-content][aria-expanded]").forEach((item) => {
      if (item.scrollHeight > item.clientHeight) {
        item.classList.add("expandable-content--expandable");
      } else {
        item.setAttribute("aria-expanded", "true");
      }
    });
  }

  /**
   * Toggle expandable content
   */
  _toggleExpandableContent(event, expandableButton) {
    const parentSection = this.querySelector("[expandable-content]");
    if (!parentSection) return;

    if (parentSection.getAttribute("aria-expanded") === "true") {
      // Close
      parentSection.setAttribute("aria-expanded", "false");
      parentSection.style["max-height"] = `${parentSection.offsetHeight}px`;
      parentSection.offsetHeight;
      parentSection.style["max-height"] = null;
      
      const expandableText = expandableButton.querySelector("[data-view-more]");
      if (expandableText) {
        expandableText.innerHTML = expandableText.getAttribute("data-view-more");
      }
      
      const parentCard = parentSection.closest(".card");
      if (parentCard) {
        const amountToScroll = parentCard.getBoundingClientRect().top - 15 - parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height"));
        window.scrollBy({ top: amountToScroll, behavior: "smooth" });
      }
    } else {
      // Open
      parentSection.setAttribute("aria-expanded", "true");
      if (expandableButton.previousElementSibling) {
        expandableButton.previousElementSibling.style["margin-bottom"] = `${parseInt(expandableButton.clientHeight)}px`;
      }
      Animation.slideDown(parentSection, null, "max-height");
      
      const expandableText = expandableButton.querySelector("[data-view-more]");
      if (expandableText) {
        expandableText.innerHTML = expandableText.getAttribute("data-view-less");
      }
    }
  }
}

if (!customElements.get("cos-expandable-farmacii")) customElements.define("cos-expandable-farmacii", ExpandableFarmaciiManager);
